import { useCallback, useMemo } from 'react';
import { atom, useAtom } from 'jotai';
import { append, exclude, includes, remove, unique } from '../../utils/array';
import { getEntries } from '../../utils/record';

type LocationFilterType = (typeof LOCATION_FILTER_TYPES)[number];

type AttributeFilterType =
  | 'endActivityTypes'
  | 'longestDistanceModes'
  | 'genderIds'
  | 'ageGroups'
  | 'nationalities';

type RangeFilterType =
  | 'traveledTime'
  | 'traveledDistance'
  | 'activityTime'
  | 'depTime'
  | 'arrTime'
  | 'passageTime'
  | 'stayingTime';

type AreaFilter = {
  areaKeyCodes: string[];
  customAreaKeys: string[];
};

type LinkFilter = {
  wayIds: string[];
};

type DataFilter = {
  start: AreaFilter;
  end: AreaFilter;
  pass: AreaFilter & LinkFilter;
  home: AreaFilter;
  work: AreaFilter;
  visit: AreaFilter;
  passRelation: 'and' | 'or' | null;
  endActivityTypes: string[];
  longestDistanceModes: string[];
  genderIds: string[];
  ageGroups: string[];
  nationalities: string[];
  traveledTime: [number, number] | null;
  traveledDistance: [number, number] | null;
  activityTime: [number, number] | null;
  depTime: [number, number] | null;
  arrTime: [number, number] | null;
  passageTime: [number, number] | null;
  stayingTime: [number, number] | null;
  stopBy: 'before' | 'after' | 'all' | null;
};

type UseDataFilter = {
  dataFilter: DataFilter;
  filteredAreaKeyCodes: string[];
  filteredCustomAreaKeys: string[];
  filteredLinkWayIds: string[];
  agentFilterCount: number;
  tripFilterCount: number;
  advancedFilterCount: number;
  setDataFilter: (dataFilter: DataFilter) => void;
  updateAreaFilter: (
    keyCodes: string[],
    types: Partial<Record<LocationFilterType, boolean>>
  ) => void;
  updateCustomAreaFilter: (
    keys: string[],
    types: Partial<Record<LocationFilterType, boolean>>
  ) => void;
  updateLinkFilter: (wayIds: string[], types: Record<'pass', boolean>) => void;
  deleteAreaFilter: (keyCode: string, type: LocationFilterType | 'all') => void;
  deleteCustomAreaFilter: (
    key: string,
    type: LocationFilterType | 'all'
  ) => void;
  deleteLinkFilter: (wayId: string, type: 'pass') => void;
  setPassRelation: (relation: 'and' | 'or') => void;
  addAttributeFilter: <K extends AttributeFilterType>(
    key: K,
    value: DataFilter[K][number]
  ) => void;
  deleteAttributeFilter: <K extends AttributeFilterType>(
    key: K,
    value: DataFilter[K][number]
  ) => void;
  setRangeFilter: (
    key: RangeFilterType,
    range: [number, number] | null
  ) => void;
  setStopBy: (stopBy: DataFilter['stopBy']) => void;
  clearAllFilters: () => void;
};

const LOCATION_FILTER_TYPES = [
  'start',
  'end',
  'pass',
  'home',
  'work',
  'visit',
] as const;

const EMPTY_DATA_FILTER: DataFilter = {
  start: {
    areaKeyCodes: [],
    customAreaKeys: [],
  },
  end: {
    areaKeyCodes: [],
    customAreaKeys: [],
  },
  pass: {
    areaKeyCodes: [],
    customAreaKeys: [],
    wayIds: [],
  },
  home: {
    areaKeyCodes: [],
    customAreaKeys: [],
  },
  work: {
    areaKeyCodes: [],
    customAreaKeys: [],
  },
  visit: {
    areaKeyCodes: [],
    customAreaKeys: [],
  },
  passRelation: null,
  endActivityTypes: [],
  longestDistanceModes: [],
  genderIds: [],
  ageGroups: [],
  nationalities: [],
  traveledTime: null,
  traveledDistance: null,
  activityTime: null,
  depTime: null,
  arrTime: null,
  passageTime: null,
  stayingTime: null,
  stopBy: null,
};

const dataFilterAtom = atom<DataFilter>(EMPTY_DATA_FILTER);

const useDataFilter = (): UseDataFilter => {
  const [dataFilter, setDataFilter] = useAtom(dataFilterAtom);

  const filteredAreaKeyCodes = useMemo((): string[] => {
    return unique([
      ...dataFilter.start.areaKeyCodes,
      ...dataFilter.end.areaKeyCodes,
      ...dataFilter.pass.areaKeyCodes,
      ...dataFilter.home.areaKeyCodes,
      ...dataFilter.work.areaKeyCodes,
      ...dataFilter.visit.areaKeyCodes,
    ]);
  }, [
    dataFilter.start.areaKeyCodes,
    dataFilter.end.areaKeyCodes,
    dataFilter.pass.areaKeyCodes,
    dataFilter.home.areaKeyCodes,
    dataFilter.work.areaKeyCodes,
    dataFilter.visit.areaKeyCodes,
  ]);

  const filteredCustomAreaKeys = useMemo((): string[] => {
    return unique([
      ...dataFilter.start.customAreaKeys,
      ...dataFilter.end.customAreaKeys,
      ...dataFilter.pass.customAreaKeys,
      ...dataFilter.home.customAreaKeys,
      ...dataFilter.work.customAreaKeys,
      ...dataFilter.visit.customAreaKeys,
    ]);
  }, [
    dataFilter.start.customAreaKeys,
    dataFilter.end.customAreaKeys,
    dataFilter.pass.customAreaKeys,
    dataFilter.home.customAreaKeys,
    dataFilter.work.customAreaKeys,
    dataFilter.visit.customAreaKeys,
  ]);

  const filteredLinkWayIds = dataFilter.pass.wayIds;

  const agentFilterCount = [
    dataFilter.genderIds.length > 0,
    dataFilter.ageGroups.length > 0,
    dataFilter.nationalities.length > 0,
    dataFilter.home.areaKeyCodes.length > 0,
    dataFilter.work.areaKeyCodes.length > 0,
    dataFilter.visit.areaKeyCodes.length > 0,
    dataFilter.home.customAreaKeys.length > 0,
    dataFilter.work.customAreaKeys.length > 0,
    dataFilter.visit.customAreaKeys.length > 0,
  ].filter(Boolean).length;

  const tripFilterCount = [
    dataFilter.endActivityTypes.length > 0,
    dataFilter.longestDistanceModes.length > 0,
    dataFilter.traveledTime !== null,
    dataFilter.traveledDistance !== null,
    dataFilter.depTime !== null,
    dataFilter.arrTime !== null,
    dataFilter.start.areaKeyCodes.length > 0,
    dataFilter.end.areaKeyCodes.length > 0,
    dataFilter.pass.areaKeyCodes.length > 0,
    dataFilter.start.customAreaKeys.length > 0,
    dataFilter.end.customAreaKeys.length > 0,
    dataFilter.pass.customAreaKeys.length > 0,
    dataFilter.pass.wayIds.length > 0,
    dataFilter.activityTime !== null,
    dataFilter.stayingTime !== null,
    dataFilter.passageTime !== null,
  ].filter(Boolean).length;

  const advancedFilterCount = [dataFilter.stopBy !== null].filter(
    Boolean
  ).length;

  const updateAreaFilter = useCallback(
    (
      keyCodes: string[],
      types: Partial<Record<LocationFilterType, boolean>>
    ): void => {
      setDataFilter((currentFilter: DataFilter): DataFilter => {
        return {
          ...currentFilter,
          ...Object.fromEntries(
            getEntries(types).map(
              ([type, value]: [LocationFilterType, boolean]): [
                LocationFilterType,
                DataFilter[LocationFilterType],
              ] => {
                if (value === true) {
                  return [
                    type,
                    {
                      ...currentFilter[type],
                      areaKeyCodes: append(
                        currentFilter[type].areaKeyCodes,
                        keyCodes
                      ),
                    },
                  ];
                }
                if (value === false) {
                  return [
                    type,
                    {
                      ...currentFilter[type],
                      areaKeyCodes: remove(
                        currentFilter[type].areaKeyCodes,
                        keyCodes
                      ),
                    },
                  ];
                }
                return [type, currentFilter[type]];
              }
            )
          ),
        };
      });
    },
    [setDataFilter]
  );

  const updateCustomAreaFilter = useCallback(
    (
      keys: string[],
      types: Partial<Record<LocationFilterType, boolean>>
    ): void => {
      setDataFilter((currentFilter: DataFilter): DataFilter => {
        return {
          ...currentFilter,
          ...Object.fromEntries(
            getEntries(types).map(
              ([type, value]: [LocationFilterType, boolean]): [
                LocationFilterType,
                DataFilter[LocationFilterType],
              ] => {
                if (value === true) {
                  return [
                    type,
                    {
                      ...currentFilter[type],
                      customAreaKeys: append(
                        currentFilter[type].customAreaKeys,
                        keys
                      ),
                    },
                  ];
                }
                if (value === false) {
                  return [
                    type,
                    {
                      ...currentFilter[type],
                      customAreaKeys: remove(
                        currentFilter[type].customAreaKeys,
                        keys
                      ),
                    },
                  ];
                }
                return [type, currentFilter[type]];
              }
            )
          ),
        };
      });
    },
    [setDataFilter]
  );

  const updateLinkFilter = useCallback(
    (wayIds: string[], types: Record<'pass', boolean>): void => {
      setDataFilter((currentFilter: DataFilter): DataFilter => {
        return {
          ...currentFilter,
          pass: {
            ...currentFilter.pass,
            wayIds: types.pass
              ? append(currentFilter.pass.wayIds, wayIds)
              : remove(currentFilter.pass.wayIds, wayIds),
          },
        };
      });
    },
    [setDataFilter]
  );

  const deleteAreaFilter = useCallback(
    (keyCode: string, type: LocationFilterType | 'all'): void => {
      setDataFilter((currentFilter: DataFilter): DataFilter => {
        if (type === 'all') {
          return {
            ...currentFilter,
            ...Object.fromEntries(
              LOCATION_FILTER_TYPES.map(
                (
                  filterType: LocationFilterType
                ): [LocationFilterType, DataFilter[LocationFilterType]] => {
                  return [
                    filterType,
                    {
                      ...currentFilter[filterType],
                      areaKeyCodes: exclude(
                        currentFilter[filterType].areaKeyCodes,
                        keyCode
                      ),
                    },
                  ];
                }
              )
            ),
          };
        }
        return {
          ...currentFilter,
          [type]: {
            ...currentFilter[type],
            areaKeyCodes: exclude(currentFilter[type].areaKeyCodes, keyCode),
          },
        };
      });
    },
    [setDataFilter]
  );

  const deleteCustomAreaFilter = useCallback(
    (key: string, type: LocationFilterType | 'all'): void => {
      setDataFilter((currentFilter: DataFilter): DataFilter => {
        if (type === 'all') {
          return {
            ...currentFilter,
            ...Object.fromEntries(
              LOCATION_FILTER_TYPES.map(
                (
                  filterType: LocationFilterType
                ): [LocationFilterType, DataFilter[LocationFilterType]] => {
                  return [
                    filterType,
                    {
                      ...currentFilter[filterType],
                      customAreaKeys: exclude(
                        currentFilter[filterType].customAreaKeys,
                        key
                      ),
                    },
                  ];
                }
              )
            ),
          };
        }
        return {
          ...currentFilter,
          [type]: {
            ...currentFilter[type],
            customAreaKeys: exclude(currentFilter[type].customAreaKeys, key),
          },
        };
      });
    },
    [setDataFilter]
  );

  const deleteLinkFilter = useCallback(
    (wayId: string): void => {
      setDataFilter((currentFilter: DataFilter): DataFilter => {
        return {
          ...currentFilter,
          pass: {
            ...currentFilter.pass,
            wayIds: exclude(currentFilter.pass.wayIds, wayId),
          },
        };
      });
    },
    [setDataFilter]
  );

  const setPassRelation = useCallback(
    (relation: 'and' | 'or'): void => {
      setDataFilter((currentFilter: DataFilter): DataFilter => {
        return { ...currentFilter, passRelation: relation };
      });
    },
    [setDataFilter]
  );

  const addAttributeFilter = useCallback(
    <K extends AttributeFilterType>(
      key: K,
      value: DataFilter[K][number]
    ): void => {
      setDataFilter((currentFilter: DataFilter): DataFilter => {
        return {
          ...currentFilter,
          [key]: includes(currentFilter[key] as (string | number)[], value)
            ? currentFilter[key]
            : [...currentFilter[key], value],
        };
      });
    },
    [setDataFilter]
  );

  const deleteAttributeFilter = useCallback(
    <T extends AttributeFilterType>(
      key: T,
      value: DataFilter[T][number]
    ): void => {
      setDataFilter((currentFilter: DataFilter): DataFilter => {
        return {
          ...currentFilter,
          [key]: exclude(currentFilter[key] as (string | number)[], value),
        };
      });
    },
    [setDataFilter]
  );

  const setRangeFilter = useCallback(
    (key: RangeFilterType, range: [number, number] | null): void => {
      setDataFilter((currentFilter: DataFilter): DataFilter => {
        return { ...currentFilter, [key]: range };
      });
    },
    [setDataFilter]
  );

  const setStopBy = useCallback(
    (stopBy: DataFilter['stopBy']): void => {
      setDataFilter((currentFilter: DataFilter): DataFilter => {
        return { ...currentFilter, stopBy };
      });
    },
    [setDataFilter]
  );

  const clearAllFilters = useCallback((): void => {
    setDataFilter(EMPTY_DATA_FILTER);
  }, [setDataFilter]);

  return {
    dataFilter,
    filteredAreaKeyCodes,
    filteredCustomAreaKeys,
    filteredLinkWayIds,
    agentFilterCount,
    tripFilterCount,
    advancedFilterCount,
    setDataFilter,
    updateAreaFilter,
    updateCustomAreaFilter,
    updateLinkFilter,
    deleteAreaFilter,
    deleteCustomAreaFilter,
    deleteLinkFilter,
    setPassRelation,
    addAttributeFilter,
    deleteAttributeFilter,
    setRangeFilter,
    setStopBy,
    clearAllFilters,
  };
};

export type {
  DataFilter,
  AreaFilter,
  LinkFilter,
  LocationFilterType,
  AttributeFilterType,
  RangeFilterType,
};
export { useDataFilter };
